import React, { useCallback, useEffect, useState } from 'react';
import './App.css';
import { api, getToken, setToken, setUnauthorizedHandler } from './api';
import Dashboard from './pages/Dashboard';
import Accounts from './pages/Accounts';
import Credentials from './pages/Credentials';
import Composer from './pages/Composer';
import Publications from './pages/Publications';
import Analytics from './pages/Analytics';
import Users from './pages/Users';
import {
  IconDashboard,
  IconUsers,
  IconKey,
  IconPen,
  IconList,
  IconChart,
  IconUserCog,
  IconPlus,
  IconRefresh,
  IconLogout,
  IconPlug,
} from './lib/icons';

const NAV = [
  { id: 'dashboard', label: 'Painel', icon: IconDashboard },
  { id: 'accounts', label: 'Contas', icon: IconUsers },
  { id: 'credentials', label: 'APIs', icon: IconKey },
  { id: 'composer', label: 'Nova publicação', icon: IconPen },
  { id: 'publications', label: 'Publicações', icon: IconList },
  { id: 'analytics', label: 'Estatísticas', icon: IconChart },
  { id: 'users', label: 'Usuários', icon: IconUserCog, adminOnly: true },
];

const TITLES = {
  dashboard: 'Painel geral',
  accounts: 'Contas conectadas',
  credentials: 'Administração de APIs',
  composer: 'Criar publicação',
  publications: 'Todas as publicações',
  analytics: 'Estatísticas',
  users: 'Usuários do painel',
};

function App() {
  const [view, setView] = useState('dashboard');
  const [platforms, setPlatforms] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [publications, setPublications] = useState([]);
  const [summary, setSummary] = useState(null);
  const [health, setHealth] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [unauthorized, setUnauthorized] = useState(false);
  const [user, setUser] = useState(null);
  const [userDraft, setUserDraft] = useState('');
  const [passDraft, setPassDraft] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);

  const load = useCallback(async () => {
    const [me, plats, pubs, sum, accs] = await Promise.allSettled([
      api.get('/auth/me'),
      api.get('/platforms'),
      api.get('/publications'),
      api.get('/analytics/summary'),
      api.get('/accounts'),
    ]);

    const unauthorized = me.status === 'rejected' && me.reason && me.reason.status === 401;
    if (unauthorized) {
      setToken('');
      setUser(null);
      setUnauthorized(true);
      setError('');
      setLoading(false);
      return;
    }

    setUser(me.status === 'fulfilled' ? me.value : null);
    setPlatforms(plats.status === 'fulfilled' ? plats.value : []);
    setAccounts(accs.status === 'fulfilled' ? accs.value : []);
    setPublications(pubs.status === 'fulfilled' ? pubs.value : []);
    setSummary(sum.status === 'fulfilled' ? sum.value : null);

    const failures = [plats, pubs, sum, accs].filter((r) => r.status === 'rejected');
    if (failures.length > 0) {
      const first = failures[0].reason;
      setError(first && first.message ? first.message : 'Falha ao carregar dados');
    } else {
      setError('');
    }
    setUnauthorized(false);
    setLoading(false);
  }, []);

  const checkHealth = useCallback(async () => {
    try {
      setHealth(await api.get('/health'));
    } catch (err) {
      setHealth(null);
    }
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(() => {
      setToken('');
      setUser(null);
      setUnauthorized(true);
      setError('');
    });
    return () => setUnauthorizedHandler(null);
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === 'test') return undefined;
    if (!getToken()) {
      setLoading(false);
      setUnauthorized(true);
      return undefined;
    }
    load();
    checkHealth();
    const timer = setInterval(checkHealth, 20000);
    return () => clearInterval(timer);
  }, [load, checkHealth]);

  const navigate = (next) => {
    setView(next);
    window.scrollTo(0, 0);
  };

  const submitLogin = async (event) => {
    event.preventDefault();
    const username = userDraft.trim();
    const password = passDraft;
    if (!username || !password) return;
    setLoginBusy(true);
    setLoginError('');
    setToken('');
    setLoading(true);
    try {
      const session = await api.post('/auth/login', { username, password });
      setToken(session.token);
      setUser(session.user);
      await load();
    } catch (err) {
      setToken('');
      setLoginError(err.message);
      setLoading(false);
    } finally {
      setLoginBusy(false);
    }
  };

  const logout = async () => {
    try {
      await api.post('/auth/logout', {});
    } catch (err) {
      /* sessão já inválida */
    }
    setToken('');
    setUser(null);
    setUserDraft('');
    setPassDraft('');
    setLoginError('');
    setLoading(false);
    setUnauthorized(true);
  };

  if (unauthorized) {
    return (
      <div className="login-wrap">
        <form className="card login-card" onSubmit={submitLogin}>
          <span className="brand-mark">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="5" r="2.4" />
              <circle cx="5" cy="18" r="2.4" />
              <circle cx="19" cy="18" r="2.4" />
              <path d="M12 7.4v4.2M10.4 12.8 6.6 16.2M13.6 12.8l3.8 3.4" />
            </svg>
          </span>
          <h2>Marketing Hub</h2>
          <p className="subtitle">Entre com seu usuário e senha para continuar</p>
          <label className="login-label" htmlFor="login-user">
            Usuário
          </label>
          <input
            id="login-user"
            className="input"
            type="text"
            autoComplete="username"
            placeholder="seu usuário"
            value={userDraft}
            onChange={(event) => setUserDraft(event.target.value)}
          />
          <label className="login-label" htmlFor="login-pass">
            Senha
          </label>
          <input
            id="login-pass"
            className="input"
            type="password"
            autoComplete="current-password"
            placeholder="sua senha"
            value={passDraft}
            onChange={(event) => setPassDraft(event.target.value)}
          />
          {loginError ? <div className="banner err">{loginError}</div> : null}
          <button className="btn primary" type="submit" disabled={loginBusy || !userDraft.trim() || !passDraft}>
            {loginBusy ? 'Verificando…' : 'Entrar'}
          </button>
        </form>
      </div>
    );
  }

  const connected = platforms.filter((p) => p.connected).length;
  const nextSchedule = publications
    .filter((p) => p.status === 'scheduled' && p.scheduleAt)
    .sort((a, b) => new Date(a.scheduleAt) - new Date(b.scheduleAt))[0];
  const isAdmin = Boolean(user && user.role === 'admin');
  const navItems = NAV.filter((item) => !item.adminOnly || isAdmin);
  const currentView = navItems.some((item) => item.id === view) ? view : 'dashboard';

  const shared = { platforms, accounts, publications, summary, user, onRefresh: load, navigate };

  return (
    <div className="app">
      <aside className="sidebar">
        <div className="brand">
          <span className="brand-mark">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="5" r="2.4" />
              <circle cx="5" cy="18" r="2.4" />
              <circle cx="19" cy="18" r="2.4" />
              <path d="M12 7.4v4.2M10.4 12.8 6.6 16.2M13.6 12.8l3.8 3.4" />
            </svg>
          </span>
          <div>
            <strong>Marketing Hub</strong>
            <small>Publicação multi-plataforma</small>
          </div>
        </div>

        <nav className="nav">
          {navItems.map((item) => {
            const IconCmp = item.icon;
            return (
              <button
                key={item.id}
                type="button"
                className={currentView === item.id ? 'nav-item active' : 'nav-item'}
                onClick={() => navigate(item.id)}
              >
                <span className="nav-icon">
                  <IconCmp size={17} />
                </span>
                {item.label}
              </button>
            );
          })}
        </nav>

        <div className="sidebar-foot">
          <div className="api-meta">
            {user ? `${user.name || user.username} · ${user.role === 'admin' ? 'administrador' : 'editor'}` : ''}
          </div>
          <div className={`api-status ${health ? 'on' : 'off'}`}>
            <span className="dot" />
            <span className="api-status-ico">
              <IconPlug size={14} />
            </span>
            {health ? 'API conectada' : 'API offline'}
          </div>
          <div className="api-meta">
            {health ? `${health.accounts} contas · ${health.publications} publicações` : 'Backend não detectado'}
          </div>
          <div className="api-meta">
            {health ? `Modo ${health.mode === 'live' ? 'ao vivo' : 'demo'}` : ''}
          </div>
          <button type="button" className="btn ghost small" onClick={logout}>
            <span className="btn-ico">
              <IconLogout size={15} />
            </span>
            Sair
          </button>
        </div>
      </aside>

      <div className="main">
        <header className="topbar">
          <div>
            <h1>{TITLES[currentView]}</h1>
            <p className="subtitle">
              {connected} de {platforms.length || '—'} plataformas conectadas
              {nextSchedule ? ` · próxima agendada em ${new Date(nextSchedule.scheduleAt).toLocaleString('pt-BR')}` : ''}
            </p>
          </div>
          <div className="topbar-actions">
            <span className={`pill ${health ? 'ok' : 'err'}`}>
              <span className="pill-dot" />
              {health ? 'ONLINE' : 'OFFLINE'}
            </span>
            <button type="button" className="btn ghost" onClick={load}>
              <span className="btn-ico">
                <IconRefresh size={15} />
              </span>
              Atualizar
            </button>
            <button type="button" className="btn primary" onClick={() => navigate('composer')}>
              <span className="btn-ico">
                <IconPlus size={15} />
              </span>
              Publicar
            </button>
          </div>
        </header>

        <main className="content">
          {error ? (
            <div className="banner err">
              <strong>Não foi possível falar com o backend.</strong>
              <span> {error}</span>
              <button type="button" className="btn ghost" onClick={load}>
                Tentar novamente
              </button>
            </div>
          ) : null}

          {loading ? <div className="empty">Carregando…</div> : null}

          {!loading && currentView === 'dashboard' ? <Dashboard {...shared} /> : null}
          {!loading && currentView === 'accounts' ? <Accounts {...shared} /> : null}
          {!loading && currentView === 'credentials' ? <Credentials {...shared} /> : null}
          {!loading && currentView === 'composer' ? <Composer {...shared} /> : null}
          {!loading && currentView === 'publications' ? <Publications {...shared} /> : null}
          {!loading && currentView === 'analytics' ? <Analytics {...shared} /> : null}
          {!loading && currentView === 'users' && isAdmin ? <Users {...shared} /> : null}
        </main>
      </div>
    </div>
  );
}

export default App;
